// Estado de "Minhas notas" na agenda, persistido no scheduler-api (window.api.schedulers.agendaNotes).
// O store guarda o que foi carregado para os dias em exibição; as colunas filtram por dia (notesOfDay).
// As alterações são otimistas: a tela muda na hora (a animação de concluir depende disso) e, se a API
// recusar, volta ao estado anterior e mostra o erro.
import { writable, get } from 'svelte/store'
import dayjs from 'dayjs'
import type { AgendaNoteEntity, AgendaNoteInput, AgendaNoteKind } from '../../../../modules/schedulerApi/agenda-notes/agenda-note.entity'

export type AgendaNote = AgendaNoteEntity
export type { AgendaNoteInput, AgendaNoteKind }

export const agendaNotes = writable<AgendaNote[]>([])

export const dayKey = (date: Date) => dayjs(date).format('YYYY-MM-DD')

const api = () => window.api.schedulers.agendaNotes

// ---- preferências de exibição (por usuário, via window.flags) ---------------------------------------
// Valem para todas as colunas ao mesmo tempo: no semanal, abrir as esquecidas numa coluna abre em todas.
export interface NotesPrefs {
    showForgotten: boolean
    showShared: boolean
    showDone: boolean
    showNotes: boolean
}
const PREF_KEYS: { [name in keyof NotesPrefs]: string } = {
    showForgotten: 'agenda-notes-forgotten-open',
    showShared: 'agenda-notes-shared-open',
    showDone: 'agenda-notes-done-open',
    showNotes: 'agenda-notes-block-open',
}
export const notesPrefs = writable<NotesPrefs>({ showForgotten: false, showShared: false, showDone: true, showNotes: false })

let prefsLoaded = false
// chamada antes de a agenda desenhar as colunas, para elas já abrirem no estado salvo
export async function loadNotesPrefs() {
    if (prefsLoaded) return
    prefsLoaded = true
    const loaded = { ...get(notesPrefs) }
    for (const name of Object.keys(PREF_KEYS) as (keyof NotesPrefs)[]) {
        const value = await window.flags(PREF_KEYS[name], true)
        // nunca gravado: mantém o padrão
        if (value !== undefined && value !== null) loaded[name] = !!value
    }
    notesPrefs.set(loaded)
    await loadPreferredKind()
}

// ---- tipo do item novo (tarefa / lembrete / nota) ----
// O último tipo escolhido no campo de criação é lembrado por usuário e vale para todas as colunas ao
// mesmo tempo (no semanal, trocar numa troca em todas). Editar um item existente não mexe nisto.
const KIND_PREF_KEY = 'agenda-notes-kind'
const KINDS: AgendaNoteKind[] = ['task', 'reminder', 'note']
export const notesPreferredKind = writable<AgendaNoteKind>('task')

async function loadPreferredKind() {
    const value = await window.flags(KIND_PREF_KEY, true)
    if (KINDS.includes(value)) notesPreferredKind.set(value)
}

export function setPreferredKind(kind: AgendaNoteKind) {
    if (!KINDS.includes(kind) || get(notesPreferredKind) === kind) return
    notesPreferredKind.set(kind)
    window.flags(KIND_PREF_KEY, true, kind)
}

export function toggleNotesPref(name: keyof NotesPrefs) {
    const value = !get(notesPrefs)[name]
    notesPrefs.update((prefs) => ({ ...prefs, [name]: value }))
    window.flags(PREF_KEYS[name], true, String(value))
}

// ---- leitura ---------------------------------------------------------------------------------------

// só a resposta do último pedido vale: trocando de dia rápido, uma resposta atrasada não sobrescreve a atual
let loadSeq = 0
// intervalo em exibição: é o que o SSE recarrega quando alguém muda uma nota (ver handleAgendaNoteCommand)
let currentRange: { from: string; to: string } = null
// silent: recarga em segundo plano (SSE) não mostra erro na tela
export async function loadAgendaNotes(from: string, to: string, silent: boolean = false) {
    currentRange = { from, to }
    const seq = ++loadSeq
    try {
        const list = await api().findForRange(from, to)
        if (seq === loadSeq) agendaNotes.set(list || [])
    } catch (error) {
        if (seq !== loadSeq) return
        if (silent) console.log('agenda notes reload failed', error)
        else window.error(error)
    }
}

// ---- tempo real (SSE) -------------------------------------------------------------------------------
// O scheduler-api envia o comando 'agenda-note-changed' a cada alteração (ver agenda-notes.service.ts#notifyChange):
// só para a autora quando a nota é privada, para a empresa toda quando é (ou era) pública. Aqui só recarregamos o
// intervalo em exibição, e com dois cuidados:
// - eco desta aba: a alteração feita aqui já está na tela (otimista); recarregar só repetiria o que já se vê.
//   A mesma pessoa em OUTRO aparelho não é eco e recarrega normalmente.
// - gravação em andamento: uma recarga no meio poderia trazer o estado anterior e desfazer, por um instante,
//   a mudança otimista. Ela espera as gravações terminarem.
const LOCAL_ECHO_MS = 5000
const recentLocal = new Map<string, number>()
let pendingWrites = 0
let reloadWhenIdle = false
let reloadTimer = null

export function clearAgendaNotesRange() {
    currentRange = null
}

function markLocal(agendaNoteId: string) {
    recentLocal.set(agendaNoteId, Date.now())
}

function reloadCurrent() {
    if (!currentRange) return
    if (pendingWrites > 0) {
        reloadWhenIdle = true
        return
    }
    loadAgendaNotes(currentRange.from, currentRange.to, true)
}

export interface AgendaNoteCommand {
    agendaNoteId: string
    userId: string
    byUserId: string
    change: string
    date: string
    everyDay: boolean
    untilDate: string
}

export function handleAgendaNoteCommand(value: AgendaNoteCommand) {
    if (!value || !currentRange) return
    const echo = value.byUserId === window.store?.token?.userId && Date.now() - (recentLocal.get(value.agendaNoteId) || 0) < LOCAL_ECHO_MS
    if (echo) return
    const { from, to } = currentRange
    // a nota aparece no intervalo? as que se repetem, enquanto não terminaram; as outras, se são do intervalo ou
    // de antes dele (uma tarefa esquecida de outro dia também aparece na coluna)
    const relevant = value.everyDay ? !value.untilDate || value.untilDate >= from : value.date <= to
    if (!relevant) return
    // várias mudanças seguidas (ex.: alguém concluindo tarefas em sequência) viram uma recarga só
    if (reloadTimer) clearTimeout(reloadTimer)
    reloadTimer = setTimeout(reloadCurrent, 300)
}

export function isMine(note: AgendaNote) {
    return note.userId === window.store?.token?.userId
}

export const isTask = (note: AgendaNote) => (note.kind || 'task') === 'task'
// a nota é o bloco de anotações: não pertence a dia nenhum e fica na seção própria da coluna
export const isNoteBlock = (note: AgendaNote) => note.kind === 'note'

export function appliesTo(note: AgendaNote, day: string) {
    if (!note.everyDay) return note.date === day
    if (note.skipDates?.includes(day)) return false
    return !note.untilDate || day <= note.untilDate
}

export function isDone(note: AgendaNote, day: string) {
    return !!note.completions?.[day]
}

export interface NotesOfDay {
    // lembretes: valem para o dia, não têm conclusão
    reminders: AgendaNote[]
    pending: AgendaNote[]
    done: AgendaNote[]
    // tarefas comuns, minhas, de dias anteriores e nunca concluídas
    forgotten: AgendaNote[]
    // públicas de outras pessoas que valem para o dia (só tarefa e lembrete: a nota não é do dia)
    shared: AgendaNote[]
    // o bloco de anotações: minhas e as públicas da equipe, juntas, fora da conversa do dia
    notes: AgendaNote[]
}

export function notesOfDay(all: AgendaNote[], date: Date): NotesOfDay {
    const day = dayKey(date)
    // "esquecida" é o que já passou: vendo um dia futuro, a tarefa de hoje ainda não foi esquecida
    const today = dayKey(new Date())
    const limit = day < today ? day : today
    // a nota sai da conversa do dia: ela não é de dia nenhum, vai para a seção própria
    const ofDayKinds = all.filter((el) => !isNoteBlock(el))
    const mine = ofDayKinds.filter((el) => isMine(el))
    const ofDay = mine.filter((el) => appliesTo(el, day))
    const tasks = ofDay.filter(isTask)
    const byCreation = (a: AgendaNote, b: AgendaNote) => a.createdAt.localeCompare(b.createdAt)
    return {
        reminders: ofDay.filter((el) => !isTask(el)).sort(byCreation),
        pending: tasks.filter((el) => !isDone(el, day)).sort(byCreation),
        done: tasks.filter((el) => isDone(el, day)).sort((a, b) => String(b.completions[day]).localeCompare(String(a.completions[day]))),
        forgotten: mine.filter((el) => isTask(el) && !el.everyDay && el.date < limit && !isDone(el, el.date)).sort((a, b) => b.date.localeCompare(a.date)),
        shared: ofDayKinds.filter((el) => !isMine(el) && el.isPublic && appliesTo(el, day)).sort(byCreation),
        // as minhas e as que a equipe compartilhou, na mesma lista: numa clínica, as da equipe são
        // justamente as mais consultadas (senha do alarme, texto pronto de confirmação)
        notes: all.filter((el) => isNoteBlock(el) && (isMine(el) || el.isPublic)).sort(byCreation),
    }
}

// ---- escrita ---------------------------------------------------------------------------------------

function patchLocal(agendaNoteId: string, fn: (note: AgendaNote) => AgendaNote) {
    agendaNotes.update((all) => all.map((el) => (el.agendaNoteId === agendaNoteId ? fn(el) : el)))
}

// aplica a mudança na hora e desfaz se a API recusar
async function optimistic(agendaNoteId: string, apply: () => void, call: () => Promise<any>) {
    const before = get(agendaNotes)
    markLocal(agendaNoteId)
    pendingWrites++
    apply()
    try {
        return await call()
    } catch (error) {
        agendaNotes.set(before)
        window.error(error)
        return null
    } finally {
        pendingWrites--
        if (pendingWrites === 0 && reloadWhenIdle) {
            reloadWhenIdle = false
            reloadCurrent()
        }
    }
}

// criação espera a API (o id vem de lá); devolve o id, ou null se falhou
export async function addNote(input: AgendaNoteInput, date: Date): Promise<string> {
    try {
        const created = await api().createNote({ ...input, date: dayKey(date) })
        markLocal(created.agendaNoteId)
        agendaNotes.update((all) => [...all, { ...created, completions: created.completions || {} }])
        return created.agendaNoteId
    } catch (error) {
        window.error(error)
        return null
    }
}

export async function updateNote(agendaNoteId: string, input: AgendaNoteInput) {
    return optimistic(
        agendaNoteId,
        () => patchLocal(agendaNoteId, (el) => ({ ...el, ...input })),
        () => api().updateNote(agendaNoteId, input),
    )
}

export async function removeNote(agendaNoteId: string) {
    return optimistic(
        agendaNoteId,
        () => agendaNotes.update((all) => all.filter((el) => el.agendaNoteId !== agendaNoteId)),
        () => api().removeNote(agendaNoteId),
    )
}

// "Excluir apenas esta": some só do dia, continua nos outros
export async function removeOccurrence(agendaNoteId: string, day: string) {
    return optimistic(
        agendaNoteId,
        () => patchLocal(agendaNoteId, (el) => ({ ...el, skipDates: [...(el.skipDates || []), day] })),
        () => api().skipDay(agendaNoteId, day),
    )
}

// "Excluir esta e as futuras": passa a terminar na véspera; os dias anteriores continuam com ela
export async function removeFromDay(agendaNoteId: string, day: string) {
    const until = dayjs(day).subtract(1, 'day').format('YYYY-MM-DD')
    return optimistic(
        agendaNoteId,
        () => patchLocal(agendaNoteId, (el) => ({ ...el, untilDate: el.untilDate && el.untilDate < until ? el.untilDate : until })),
        () => api().endBeforeDay(agendaNoteId, day),
    )
}

// day = dia em que a conclusão vale (para uma esquecida é o dia dela, não o selecionado)
export async function toggleDone(agendaNoteId: string, day: string) {
    const note = get(agendaNotes).find((el) => el.agendaNoteId === agendaNoteId)
    if (!note) return
    const wasDone = isDone(note, day)
    const saved = await optimistic(
        agendaNoteId,
        () =>
            patchLocal(agendaNoteId, (el) => {
                const completions = { ...(el.completions || {}) }
                if (wasDone) delete completions[day]
                else completions[day] = new Date().toISOString()
                return { ...el, completions }
            }),
        () => (wasDone ? api().uncomplete(agendaNoteId, day) : api().complete(agendaNoteId, day)),
    )
    // a hora oficial da conclusão é a do servidor
    if (saved && !wasDone && saved.completions?.[day]) {
        patchLocal(agendaNoteId, (el) => ({ ...el, completions: { ...el.completions, [day]: saved.completions[day] } }))
    }
}
